import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';


const CustomBookingCreator = () => {
  const [activeTab, setActiveTab] = useState('custom'); // 'custom' or 'import'
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    service_name: '',
    engineer_id: 1,
    engineer_name: 'Trap Spade',
    date: '',
    time: '',
    end_time: '',
    price: '',
    notes: ''
  });

  // Import past session form
  const [importData, setImportData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    service_name: 'Recording Session - 2 Hours',
    engineer_id: 1,
    engineer_name: 'Trap Spade',
    date: '',
    time: '',
    price: '',
    payment_method: 'cash',
    notes: ''
  });

  const [loading, setLoading] = useState(false);
  const [bookingLink, setBookingLink] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  const engineers = [
    { id: 1, name: 'Trap Spade', rate: 50 },
    { id: 2, name: 'Chapo', rate: 40 },
    { id: 3, name: 'Dev', rate: 40 },
    { id: 4, name: 'Shotz', rate: 40 }
  ];

  const services = [
    { name: 'Recording Session - 2 Hours', hours: 2 },
    { name: 'Recording Session - 3 Hours', hours: 3 },
    { name: 'Recording Session - 5 Hours', hours: 5 },
    { name: 'Recording Session - 6 Hours', hours: 6 },
    { name: 'Mix & Master', hours: 0, fixedPrice: true },
    { name: 'Custom Session', hours: 0, custom: true }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'engineer_id') {
      const engineerId = parseInt(value);
      const engineer = engineers.find(e => e.id === engineerId);
      setFormData(prev => ({
        ...prev,
        engineer_id: engineerId,
        engineer_name: engineer.name
      }));
      
      // Auto-calculate price if service is selected
      if (formData.service_name) {
        const service = services.find(s => s.name === formData.service_name);
        if (service && service.hours > 0) {
          setFormData(prev => ({
            ...prev,
            price: (service.hours * engineer.rate).toString()
          }));
        }
      }
    } else if (name === 'service_name') {
      const service = services.find(s => s.name === value);
      const engineer = engineers.find(e => e.id === formData.engineer_id);
      
      let price = formData.price;
      if (service && service.hours > 0 && engineer) {
        price = (service.hours * engineer.rate).toString();
      }
      
      setFormData(prev => ({
        ...prev,
        service_name: value,
        price: price
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.client_name || !formData.client_email || !formData.client_phone || !formData.service_name || 
        !formData.date || !formData.time || !formData.end_time || !formData.price) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.client_email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Validate price
    if (isNaN(parseFloat(formData.price)) || parseFloat(formData.price) <= 0) {
      toast.error('Please enter a valid price');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `/api/bookings/create-custom-booking`,
        formData
      );

      if (response.data.success) {
        setBookingLink(response.data.booking_link);
        setExpiresAt(response.data.expires_at);
        toast.success(`Custom booking created and sent to ${formData.client_email}!`);
        
        // Reset form
        setFormData({
          client_name: '',
          client_email: '',
          client_phone: '',
          service_name: '',
          engineer_id: 1,
          engineer_name: 'Trap Spade',
          date: '',
          time: '',
          end_time: '',
          price: '',
          notes: ''
        });
      }
    } catch (error) {
      console.error('Error creating custom booking:', error);
      toast.error(error.response?.data?.detail || 'Failed to create custom booking');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(bookingLink);
    toast.success('Booking link copied to clipboard!');
  };

  // Handle import past session
  const handleImportSession = async (e) => {
    e.preventDefault();
    
    if (!importData.client_name || !importData.date || !importData.time || !importData.price) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `/api/bookings/import-past-session`,
        {
          name: importData.client_name,
          email: importData.client_email || `guest-${Date.now()}@hqstu.com`,
          phone: importData.client_phone || 'N/A',
          service_name: importData.service_name,
          engineer_id: importData.engineer_id,
          engineer_name: importData.engineer_name,
          date: importData.date,
          time: importData.time,
          price: parseFloat(importData.price),
          payment_method: importData.payment_method,
          notes: importData.notes,
          status: 'confirmed',
          payment_status: 'paid'
        }
      );

      if (response.data.id) {
        toast.success(`Past session imported successfully! Booking #${response.data.booking_number}`);
        
        // Reset import form
        setImportData({
          client_name: '',
          client_email: '',
          client_phone: '',
          service_name: 'Recording Session - 2 Hours',
          engineer_id: 1,
          engineer_name: 'Trap Spade',
          date: '',
          time: '',
          price: '',
          payment_method: 'cash',
          notes: ''
        });
      }
    } catch (error) {
      console.error('Error importing session:', error);
      toast.error(error.response?.data?.detail || 'Failed to import session');
    } finally {
      setLoading(false);
    }
  };

  const handleImportChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'engineer_id') {
      const engineerId = parseInt(value);
      const engineer = engineers.find(eng => eng.id === engineerId);
      setImportData(prev => ({
        ...prev,
        engineer_id: engineerId,
        engineer_name: engineer.name
      }));
    } else {
      setImportData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const timeSlots = [];
  for (let hour = 0; hour < 24; hour++) {
    const displayHour = hour === 0 ? 12 : (hour > 12 ? hour - 12 : hour);
    const period = hour >= 12 ? 'PM' : 'AM';
    timeSlots.push(`${displayHour}:00 ${period}`);
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Tab Navigation */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setActiveTab('custom')}
          className={`px-4 py-2 rounded-lg font-medium transition-all ${
            activeTab === 'custom'
              ? 'bg-purple-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          ✨ Create Custom Booking
        </button>
        <button
          onClick={() => setActiveTab('import')}
          className={`px-4 py-2 rounded-lg font-medium transition-all ${
            activeTab === 'import'
              ? 'bg-green-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          📥 Import Past Session
        </button>
      </div>

      {/* Import Past Session Tab */}
      {activeTab === 'import' && (
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
          <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
            <span className="text-2xl sm:text-3xl">📥</span>
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Import Past Session</h2>
              <p className="text-xs sm:text-sm text-gray-600">Add in-person bookings for record keeping</p>
            </div>
          </div>

          <form onSubmit={handleImportSession} className="space-y-3 sm:space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Client Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="client_name"
                  value={importData.client_name}
                  onChange={handleImportChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-900"
                  placeholder="Client name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Client Email (Optional)
                </label>
                <input
                  type="email"
                  name="client_email"
                  value={importData.client_email}
                  onChange={handleImportChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-900"
                  placeholder="client@example.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone (Optional)
                </label>
                <input
                  type="tel"
                  name="client_phone"
                  value={importData.client_phone}
                  onChange={handleImportChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-900"
                  placeholder="Phone number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Service <span className="text-red-500">*</span>
                </label>
                <select
                  name="service_name"
                  value={importData.service_name}
                  onChange={handleImportChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-900"
                >
                  {services.map((service, idx) => (
                    <option key={idx} value={service.name}>{service.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Engineer <span className="text-red-500">*</span>
                </label>
                <select
                  name="engineer_id"
                  value={importData.engineer_id}
                  onChange={handleImportChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-900"
                >
                  {engineers.map(eng => (
                    <option key={eng.id} value={eng.id}>{eng.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="date"
                  value={importData.date}
                  onChange={handleImportChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-900"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Time <span className="text-red-500">*</span>
                </label>
                <select
                  name="time"
                  value={importData.time}
                  onChange={handleImportChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-900"
                  required
                >
                  <option value="">Select time</option>
                  {timeSlots.map((slot, idx) => (
                    <option key={idx} value={slot}>{slot}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount Paid <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="price"
                  value={importData.price}
                  onChange={handleImportChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-900"
                  placeholder="100"
                  min="0"
                  step="0.01"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Method
                </label>
                <select
                  name="payment_method"
                  value={importData.payment_method}
                  onChange={handleImportChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-900"
                >
                  <option value="cash">Cash</option>
                  <option value="zelle">Zelle</option>
                  <option value="venmo">Venmo</option>
                  <option value="cashapp">Cash App</option>
                  <option value="stripe">Stripe</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes (Optional)
              </label>
              <textarea
                name="notes"
                value={importData.notes}
                onChange={handleImportChange}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-900"
                placeholder="Any notes about this session..."
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Importing...' : '📥 Import Session'}
            </button>
          </form>

          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-3">
              <span className="text-2xl">ℹ️</span>
              <div>
                <h4 className="font-semibold text-blue-900 mb-1">About Importing Sessions</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>✓ Use this to add walk-in or in-person bookings</li>
                  <li>✓ Sessions are marked as confirmed and paid</li>
                  <li>✓ Perfect for record keeping and analytics</li>
                  <li>✓ Email is optional for guest clients</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Booking Tab */}
      {activeTab === 'custom' && (
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
        <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
          <span className="text-2xl sm:text-3xl">✨</span>
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Custom Booking Creator</h2>
            <p className="text-xs sm:text-sm text-gray-600">Create a personalized booking link for your client</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
          {/* Client Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                Client Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="client_name"
                value={formData.client_name}
                onChange={handleInputChange}
                className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900"
                placeholder="John Doe"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="client_email"
                value={formData.client_email}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900"
                placeholder="john@example.com"
                required
              />
            </div>
          </div>

          {/* Phone Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Client Phone <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              name="client_phone"
              value={formData.client_phone}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900"
              placeholder="+1 (555) 123-4567"
              required
            />
          </div>

          {/* Session Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Service <span className="text-red-500">*</span>
              </label>
              <select
                name="service_name"
                value={formData.service_name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900"
                required
              >
                <option value="">Select a service</option>
                {services.map((service) => (
                  <option key={service.name} value={service.name}>
                    {service.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Engineer <span className="text-red-500">*</span>
              </label>
              <select
                name="engineer_id"
                value={formData.engineer_id}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900"
                required
              >
                {engineers.map((engineer) => (
                  <option key={engineer.id} value={engineer.id}>
                    {engineer.name} (${engineer.rate}/hr)
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Time <span className="text-red-500">*</span>
              </label>
              <select
                name="time"
                value={formData.time}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900"
                required
              >
                <option value="">Select start time</option>
                {timeSlots.map((slot) => (
                  <option key={slot} value={slot}>
                    {slot}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* End Time */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Time <span className="text-red-500">*</span>
            </label>
            <select
              name="end_time"
              value={formData.end_time}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900"
              required
            >
              <option value="">Select end time</option>
              {timeSlots.map((slot) => (
                <option key={slot} value={slot}>
                  {slot}
                </option>
              ))}
            </select>
          </div>

          {/* Price */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Price ($) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="price"
              value={formData.price}
              onChange={handleInputChange}
              step="0.01"
              min="0"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900"
              placeholder="125.00"
              required
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Special Notes (Optional)
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900"
              placeholder="Any special instructions or notes for the client..."
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 px-6 rounded-md font-semibold hover:from-purple-700 hover:to-pink-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span>Creating Booking...</span>
              </>
            ) : (
              <>
                <span>✉️</span>
                <span>Create & Send Booking Link</span>
              </>
            )}
          </button>
        </form>

        {/* Success Message with Link - inside the custom booking tab */}
        {bookingLink && (
          <div className="bg-green-50 border border-green-200 p-6 rounded-lg mt-4">
            <div className="flex items-start gap-3">
              <span className="text-3xl">✅</span>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-green-800 mb-2">
                  Booking Link Created Successfully!
                </h3>
                <p className="text-sm text-green-700 mb-3">
                  The booking link has been sent to the client's email. Link expires in 4 hours.
                </p>
                <div className="bg-white border border-green-200 rounded-md p-3 mb-3">
                  <p className="text-xs text-gray-600 mb-1">Booking Link:</p>
                  <p className="text-sm text-purple-600 break-all font-mono">{bookingLink}</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={copyToClipboard}
                    className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 transition-colors text-sm font-medium"
                  >
                    📋 Copy Link
                  </button>
                  <a
                    href={bookingLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors text-sm font-medium"
                  >
                    🔗 Open Link
                  </a>
                </div>
                {expiresAt && (
                  <p className="text-xs text-gray-500 mt-3">
                    ⏰ Expires at: {new Date(expiresAt).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mt-4">
          <div className="flex items-start gap-3">
            <span className="text-2xl">ℹ️</span>
            <div>
              <h4 className="font-semibold text-blue-900 mb-1">How It Works</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>✓ Fill out the booking details and click "Create & Send"</li>
                <li>✓ Client receives an email with a unique booking link</li>
                <li>✓ Client reviews details and completes payment via Stripe</li>
                <li>✓ Booking is created and awaits your approval in the admin panel</li>
                <li>⚠️ Link expires after 4 hours for security</li>
                <li>🎁 <strong>Clients are encouraged to create an account for rewards</strong></li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default CustomBookingCreator;
